/**
 * subscriptionHelper.js — Centralized test-unlock and subscription status utility
 *
 * Provides a single source of truth for test access checks across all components
 * (Dashboard, WritingTest, SpeakingTest, ListeningTest, ReadingTest, dashboardpte).
 *
 * Supports both:
 *   - Stripe one-time purchases (via paidTests array in localStorage)
 *   - Google Play subscriptions (via playSubscription object in localStorage)
 */

const PLAY_STORE_MANAGE_URL = "https://play.google.com/store/account/subscriptions";
const STRIPE_ALLOWED_PACKAGES = [
    "7-DAYS-PACKAGE",
    "15-DAYS-PACKAGE",
    "30-DAYS-PACKAGE",
    "60-DAYS-PACKAGE",
    "90-DAYS-PACKAGE",
    "15-DAYS-PTE-PACKAGE",
    "30-DAYS-PTE-PACKAGE",
    "60-DAYS-PTE-PACKAGE",
    "90-DAYS-PTE-PACKAGE",
    "15-DAYS-PTE",
    "30-DAYS-PTE",
    "60-DAYS-PTE",
    "90-DAYS-PTE",
    "IELTS-MOCK-PACKAGE",
    "SELF-PACED-COURSE-30CAD",
    "CELPIP-COURSE-PACKAGE",
    "PTE-COURSE-PACKAGE",
    "IELTS-COURSE-PACKAGE"
];
// Canonical Play Console product ID: "weekly_premium".
// "weekly_full_access" and "engdemy_weekly_full_access" are legacy values that
// may still exist in old MongoDB documents written before the ID was finalised.
// They are kept here ONLY for backward compatibility — do NOT use them in new code
// and do NOT register them as Play Console products.
const PLAY_ALLOWED_PACKAGES = ["weekly_premium", "weekly_full_access", "engdemy_weekly_full_access"];
const FREE_TEST_LIMIT = 2; // Tests 1-2 are always free

// ─── Play Subscription localStorage helpers ─────────────────────────────────

/**
 * Get the cached Google Play subscription state from localStorage.
 * @returns {{ isActive: boolean, expiresAt: string|null, productId: string|null, paidTests: number[] } | null}
 */
export function getPlaySubscription() {
    try {
        const raw = localStorage.getItem("playSubscription");
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/**
 * Store the Google Play subscription state in localStorage.
 * Called by Dashboard after fetching /api/google-play/subscription-status/:userId.
 * @param {object} data — The subscription status response from the backend
 */
export function setPlaySubscription(data) {
    try {
        localStorage.setItem("playSubscription", JSON.stringify(data));
    } catch (err) {
        console.error("[SubscriptionHelper] Failed to save playSubscription:", err);
    }
}

/**
 * Clear the cached Play subscription (e.g., on logout).
 */
export function clearPlaySubscription() {
    localStorage.removeItem("playSubscription");
}

// ─── Subscription status checks ─────────────────────────────────────────────

/**
 * Helper to get currently logged-in user from localStorage.
 */
export function getStoredUser() {
    if (typeof window === "undefined") return null;
    try {
        const rawUser = localStorage.getItem("user_info");
        if (rawUser) {
            const parsed = JSON.parse(rawUser);
            if (parsed && typeof parsed === "object") {
                return parsed.result || parsed;
            }
        }
        const rawAuth = localStorage.getItem("authData");
        if (rawAuth) {
            const parsed = JSON.parse(rawAuth);
            if (parsed && typeof parsed === "object") {
                return parsed.result || parsed;
            }
        }
    } catch {
        return null;
    }
    return null;
}

/**
 * Helper to get expiry Date for a specific test from testValidity (Map or Object).
 * @param {Map|object} testValidity
 * @param {number|string} testNum
 * @returns {Date|null}
 */
export function getTestValidityExpiry(testValidity, testNum) {
    if (!testValidity) return null;
    const key = testNum !== undefined && testNum !== null ? testNum.toString() : null;
    if (key) {
        let val = null;
        if (typeof testValidity.get === "function") {
            val = testValidity.get(key);
        } else if (typeof testValidity === "object") {
            val = testValidity[key];
        }
        if (val) {
            const d = new Date(val);
            if (!isNaN(d.getTime())) return d;
        }
    }
    return null;
}

/**
 * Helper to get the latest expiry Date across all tests in testValidity.
 * @param {Map|object} testValidity
 * @returns {Date|null}
 */
export function getLatestTestValidityExpiry(testValidity) {
    if (!testValidity) return null;
    let latest = null;
    try {
        const entries = typeof testValidity.entries === "function"
            ? Array.from(testValidity.entries())
            : Object.entries(testValidity);
        for (const [key, value] of entries) {
            if (key === "purchaseToken") continue;
            const d = new Date(value);
            if (!isNaN(d.getTime())) {
                if (!latest || d > latest) {
                    latest = d;
                }
            }
        }
    } catch {
        // ignore
    }
    return latest;
}

/**
 * Check if the user's subscription (Stripe, package, Google Play, or testValidity) is expired.
 * @param {object} [currentUser] — Optional user object
 * @returns {boolean}
 */
export function isSubscriptionExpired(currentUser) {
    const user = currentUser || getStoredUser();
    const playSub = getPlaySubscription();

    // Check Play subscription expiry
    if (playSub?.expiresAt) {
        const playExp = new Date(playSub.expiresAt);
        if (!isNaN(playExp.getTime()) && playExp <= new Date()) {
            return true;
        }
    }

    // Check Stripe/User subscription expiry
    const expiry = user?.subscriptionExpiresAt || user?.subscription?.expiresAt;
    if (expiry) {
        const expDate = new Date(expiry);
        if (!isNaN(expDate.getTime()) && expDate <= new Date()) {
            return true;
        }
    }

    // Check testValidity expiry
    if (user?.testValidity) {
        const latestExp = getLatestTestValidityExpiry(user.testValidity);
        if (latestExp && latestExp <= new Date()) {
            return true;
        }
    }

    return false;
}

/**
 * Check if the user has an active Google Play subscription
 * that hasn't expired yet.
 * @returns {boolean}
 */
export function hasActivePlaySubscription() {
    const sub = getPlaySubscription();
    if (!sub || !sub.isActive) return false;

    // If there's an expiresAt, ensure it's in the future
    if (sub.expiresAt) {
        return new Date(sub.expiresAt) > new Date();
    }

    // Active flag without expiry — trust the server's isActive
    return true;
}

/**
 * Check if the user has an active Stripe subscription
 * (any of the time-based packages).
 * @param {object} [currentUser] — Optional user object with packageName
 * @returns {boolean}
 */
export function hasActiveStripeSubscription(currentUser) {
    const user = currentUser || getStoredUser();
    const userPackage = user?.packageName || (typeof window !== "undefined" ? localStorage.getItem("userPackage") : null);
    if (!userPackage) return false;

    // If subscription has an expiry date and it has passed, it is no longer active
    if (isSubscriptionExpired(user)) {
        return false;
    }

    return STRIPE_ALLOWED_PACKAGES.includes(userPackage) || userPackage.toUpperCase().includes("DAYS");
}

/**
 * Check if the user has ANY active subscription (Stripe OR Google Play).
 * @param {object} [currentUser] — Optional user object
 * @returns {boolean}
 */
export function hasActiveSubscription(currentUser) {
    return hasActiveStripeSubscription(currentUser) || hasActivePlaySubscription();
}

// ─── Test unlock logic (THE central authority) ──────────────────────────────

/**
 * Determine if a specific test number is unlocked for the current user.
 *
 * Priority order:
 *   1. Tests 1-2 are always free
 *   2. If subscription/access is expired, tests > 2 CANNOT be started
 *   3. Active Google Play subscription → all tests unlocked
 *   4. Active Stripe subscription → all tests unlocked
 *   5. Direct check for isPaidUser flag in localStorage (only if not expired)
 *   6. Stripe paidTests array or "all" from localStorage (only if not expired)
 *
 * @param {number} testNum — The test number (1-50)
 * @param {object} [currentUser] — Optional user object
 * @returns {boolean} — true if the test is accessible
 */
export function isTestUnlocked(testNum, currentUser) {
    // Free tests are always accessible
    if (testNum <= FREE_TEST_LIMIT) return true;

    const user = currentUser || getStoredUser();

    // If subscription is expired, paid tests (3-50) CANNOT be started
    if (isSubscriptionExpired(user)) {
        return false;
    }

    // Check specific test validity in testValidity if present
    if (user?.testValidity) {
        const testExp = getTestValidityExpiry(user.testValidity, testNum);
        if (testExp && testExp <= new Date()) {
            return false;
        }
    }

    // Google Play subscription unlocks everything
    if (hasActivePlaySubscription()) return true;

    // Active Stripe subscription unlocks everything
    if (hasActiveStripeSubscription(user)) return true;

    // If user object is provided and has paidTests array, respect it directly (do not fall back to stale localStorage)
    if (user && Array.isArray(user.paidTests)) {
        if (user.paidTests.length === 0) {
            return false;
        }
        const num = Number(testNum);
        return user.paidTests.includes(num) || user.paidTests.includes(testNum);
    }

    // Direct check for paid user flag in localStorage (only if not expired and no contradicting user object)
    if (typeof window !== "undefined" && localStorage.getItem("isPaidUser") === "true") {
        return true;
    }

    // Stripe / direct purchase — check paidTests
    try {
        const raw = typeof window !== "undefined" ? localStorage.getItem("paidTests") : null;
        if (!raw) return false;
        if (raw === '"all"' || raw === 'all') return true;
        const paidTests = JSON.parse(raw);
        if (paidTests === "all") return true;
        if (Array.isArray(paidTests)) {
            if (paidTests.length === 0) return false;
            const num = Number(testNum);
            return paidTests.includes(num) || paidTests.includes(testNum);
        }
        return false;
    } catch {
        return false;
    }
}

/**
 * Get the Google Play Store subscription management URL.
 * @returns {string}
 */
export function getManageSubscriptionUrl() {
    return PLAY_STORE_MANAGE_URL;
}

// ─── Exports for testing / constants ────────────────────────────────────────

const subscriptionConfig = {
    PLAY_STORE_MANAGE_URL,
    STRIPE_ALLOWED_PACKAGES,
    PLAY_ALLOWED_PACKAGES,
    FREE_TEST_LIMIT,
};

export default subscriptionConfig;
